import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { mergeMap, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AsyncPipe, NgIf } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  dateTimeFacade,
  dateTimeHelper,
  EBookingPageType,
  ecNoOnlyWhiteSpacesValidator,
  ERole,
  ETimeNumericFormat,
  getContrastYIQ,
  injectEnvironment,
  LayoutService,
  NotificationService,
  ResponsiveService,
} from '@cf/core';
import { EventService } from '@cf/features/schedules';
import { LinkComponent } from '@cf/shared/ui/button';
import { LoaderContainerComponent } from '@cf/shared/ui/loading';
import { AvatarComponent } from '@cf/shared/ui/logos';
import { DurationPipe, TextComponent } from '@cf/shared/ui/typography';
import type {
  IBooking,
  IBookingMember,
  IEventContainer,
  ITimeslots,
  SchemaAppEventDetail,
  SchemaMeetingInfo,
} from '@cf/temp/services';
import {
  AccountService,
  AppEventApiService,
  AuthService,
  ETimezoneSettings,
  EventStatusEnum,
  TimeFormatEnum,
  UsersService,
} from '@cf/temp/services';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import {
  BookingAvailabilityComponent,
  BookingCalendarComponent,
  BookingConfirmationComponent,
  BookingFooterComponent,
  BookingPickerComponent,
  BookingStepsComponent,
  RescheduleReasonComponent,
} from '../../components';
import { BookingApiService } from '../../services/booking-api.service';
import { BookingInformationComponent } from '../../shared/booking-information/booking-information.component';

@Component({
  selector: 'cf-reschedule-booking',
  templateUrl: './reschedule-booking-page.component.html',
  styleUrls: ['./reschedule-booking-page.component.scss', '../booking/booking-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    AvatarComponent,
    TextComponent,
    DurationPipe,
    BookingInformationComponent,
    LinkComponent,
    NgxTolgeeModule,
    AsyncPipe,
    BookingStepsComponent,
    LoaderContainerComponent,
    BookingCalendarComponent,
    BookingPickerComponent,
    BookingAvailabilityComponent,
    BookingFooterComponent,
    BookingConfirmationComponent,
    RescheduleReasonComponent,
  ],
})
export class RescheduleBookingPageComponent implements OnInit, OnDestroy {
  public readonly authService = inject(AuthService);
  public readonly accountService = inject(AccountService);
  public readonly responsiveService = inject(ResponsiveService);
  public readonly eventApiService = inject(AppEventApiService);
  public readonly bookingApiService = inject(BookingApiService);
  public readonly eventService = inject(EventService);
  public readonly notificationService = inject(NotificationService);
  public readonly cd = inject(ChangeDetectorRef);
  public readonly route = inject(ActivatedRoute);
  public readonly router = inject(Router);
  public readonly layoutService = inject(LayoutService);
  public readonly fb = inject(FormBuilder);
  public readonly usersService = inject(UsersService);
  translateService = inject(TranslateService);
  config = injectEnvironment();

  event!: SchemaMeetingInfo & SchemaAppEventDetail;
  timeslots!: ITimeslots[];
  events: IEventContainer[] = [];
  timezone!: string;
  participants!: IBookingMember[];

  selectedSlot!: ITimeslots;
  selectedTime!: string;
  selectedDuration!: string;
  hasFreeSlots!: boolean;

  hideDescription = false;
  submitted = false;
  isTouch$ = this.responsiveService.isTouch$;
  dateRange = {
    from_date: dateTimeFacade.startOf('month').toSQLDate(),
    to_date: dateTimeFacade.endOf('month').toSQLDate(),
  };

  form!: FormGroup;

  readonly EMeetingType = EBookingPageType;
  readonly ETimezoneSettings = ETimezoneSettings;
  readonly getContrastYIQ = getContrastYIQ;

  private destroyRef = inject(DestroyRef);

  constructor() {
    this.layoutService.hideHeader.next(true);
    this.form = this.fb.group({
      reason_for_change: [
        '',
        [Validators.required, ecNoOnlyWhiteSpacesValidator, Validators.maxLength(500)],
      ],
    });
  }

  get stepIndex(): number {
    if (!this.selectedSlot) {
      return 0;
    } else if (!this.selectedTime) {
      return 1;
    } else {
      return 2;
    }
  }

  get eventDescription(): string {
    return this.event.description ?? this.event.meeting_template?.description ?? '';
  }

  get timeFormat(): ETimeNumericFormat {
    return this.event.time_format === TimeFormatEnum['12h']
      ? ETimeNumericFormat.TIME_12
      : ETimeNumericFormat.TIME_24;
  }

  get eventTime() {
    return dateTimeHelper.getTime(this.event.start, this.timeFormat, false, this.event.timezone);
  }

  get selectedDate() {
    if (this.selectedSlot) {
      return dateTimeHelper.getDateFromISO(this.selectedSlot.date).toJSDate();
    }

    return null;
  }

  get rejected() {
    return this.event?.status === EventStatusEnum.REJECTED;
  }

  ngOnInit() {
    if (this.config.production && this.config.envName === 'dev') {
      window.location.href =
        this.config.bookingUrl +
        'reschedule/' +
        (this.route.snapshot.queryParams.rescheduleEventToken ||
          this.route.snapshot.queryParams.rescheduleEvent);
    }
    this.getData(this.route.snapshot.queryParams.rescheduleEventToken);

    this.authService.isAuthorized$.subscribe((res) => {
      if (res && this.selectedSlot) {
        this.getEvents(this.selectedSlot);
      }
    });
  }

  ngOnDestroy() {
    this.layoutService.hideHeader.next(false);
  }

  stepChange(index: number) {
    if (index === 0) {
      this.selectedSlot = undefined as any;
      this.selectedTime = undefined as any;
      this.selectedDuration = undefined as any;
    }
    if (index === 1) {
      this.selectedTime = undefined as any;
      this.selectedDuration = undefined as any;
    }
  }

  back() {
    if (this.selectedTime) {
      this.selectedSlot = undefined as any;
      this.selectedTime = undefined as any;
      this.selectedDuration = undefined as any;
    } else if (this.selectedSlot) {
      this.selectedSlot = undefined as any;
    }
  }

  public setFreeSlot() {
    this.selectedSlot = this.timeslots?.find((slot) => {
      return !!slot.time_slots?.length;
    }) as ITimeslots;
  }

  applyDate(date: { from_date: string; to_date: string }) {
    this.dateRange = date;
    this.getTimeslots(
      this.timezone,
      this.selectedDuration
        ? dateTimeFacade.decimalToDuration({ minute: +this.selectedDuration })
        : this.event.duration,
    );
  }

  durationChanged(duration: string) {
    this.getTimeslots(this.timezone, dateTimeFacade.decimalToDuration({ minute: +duration }));
    this.selectedDuration = duration;
  }

  getEvents(slot: ITimeslots) {
    if (slot) {
      this.selectedSlot = slot;
    }
    const dto = {
      from_date: this.selectedSlot.date,
      to_date: this.selectedSlot.date,
      timezone: this.timezone,
    };

    if (this.authService.isAuthorized()) {
      this.eventService
        .events({
          ...dto,
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          this.events = res ?? [];
          this.cd.detectChanges();
        });
    }
  }

  getTimeslots(timezone: string, duration = this.event.duration) {
    this.timezone = timezone;
    this.handleEventTime();

    if (this.selectedSlot) {
      this.getEvents(this.selectedSlot);
    }

    // TODO: Check Here
    // if (
    //   this.event.type === EBookingPageType.GROUP ||
    //   this.event.type === EBookingPageType.FLEXIBLE
    // ) {
    //   this.timeslots = [];
    //   return;
    // }

    // if (this.event.meeting_template?.path) {
    this.bookingApiService
      .hasFreeSlots(this.event.meeting_template!.path!, this.event.meeting_template!.prefix!, {
        duration: dateTimeFacade.decimalToDuration({ minute: 5 }),
      })
      .subscribe((res) => {
        this.hasFreeSlots = res;
        this.cd.detectChanges();
      });

    this.bookingApiService
      .timeslots(this.event.meeting_template!.path!, this.event.meeting_template!.prefix!, {
        timezone,
        duration,
        rescheduling_event_id: this.event.id,
        ...this.dateRange,
      })
      .subscribe((res) => {
        this.timeslots = res;
        if (this.selectedSlot) {
          const slot = res.find((s) => s.date === this.selectedSlot.date);

          if (slot) {
            this.selectedSlot = slot as any;
          } else if (res.length) {
            this.selectedSlot = res.find(Boolean) as any;
          } else {
            this.selectedSlot = {
              ...this.selectedSlot,
              time_slots: [],
              free_slots: [],
            };
          }
        }
        this.cd.detectChanges();
      });
    // }
  }

  focusTime(e: IEventContainer[]) {
    this.events = [...e];
  }

  confirm(data: { time: string; duration: string }) {
    this.selectedTime = dateTimeHelper.getTime(data.time, this.timeFormat, true);

    this.selectedDuration = data.duration;
  }

  submit(formValue: any) {
    const dto = {
      timezone: this.timezone,
      date: this.selectedSlot.date,
      time: dateTimeHelper.getTimeFromFormat(this.selectedTime, 24),
      duration: dateTimeFacade.decimalToDuration({
        minute: +this.selectedDuration,
      }),
      reason_for_change: formValue.reason_for_change,
    };

    if (this.route.snapshot.queryParams.rescheduleEvent) {
      this.eventApiService
        .rescheduleEvent(this.route.snapshot.queryParams.rescheduleEvent, dto)
        .subscribe((_res) => {
          // this.event.event_added_to_calendar = res.event_added_to_calendar;
          this.submitted = true;
        });
    } else {
      this.eventApiService
        .rescheduleEvent(this.route.snapshot.queryParams.rescheduleEventToken, dto)
        .subscribe((_res) => {
          // this.event.event_added_to_calendar = res.event_added_to_calendar;
          this.submitted = true;
        });
    }
  }

  private getData(token?: string): void {
    const eventId = token || this.route.snapshot.queryParams.rescheduleEvent;

    if (!eventId) {
      this.notificationService.error(this.translateService.instant('booking.no_event_id_provided'));
      void this.router.navigate(['/']);
      return;
    }

    this.eventApiService
      .event(eventId)
      .pipe(
        mergeMap((event) => {
          if (!event.can_reschedule) {
            throw new Error(this.translateService.instant('booking.the_event_cant_be_rescheduled'));
          }

          if (
            // TODO: Check Here
            // (event.type === EBookingPageType.GROUP || event.type === EBookingPageType.FLEXIBLE) &&
            event.can_reschedule &&
            !event.is_cancelled
          ) {
            return of(event as SchemaAppEventDetail & IBooking);
          }

          if (!event.meeting_template) {
            throw new Error(
              this.translateService.instant(
                'booking.the_event_cant_be_rescheduled_as_the_booking_page_has_been_removed',
              ),
            );
          }

          return this.bookingApiService
            .booking(event.meeting_template.path!, event.meeting_template.prefix!)
            .pipe(
              map(
                (booking: SchemaMeetingInfo) =>
                  ({ ...booking, ...event }) as SchemaMeetingInfo & SchemaAppEventDetail,
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          if (!res) return;

          this.event = res;
          this.timezone = res.timezone;
          this.getTimeslots(this.timezone);
          this.handleEventTime();
          this.setParticipants();
          this.cd.detectChanges();
        },
        error: (error) => {
          this.notificationService.error(error.message);
          void this.router.navigate(['/']);
        },
      });
  }

  private handleEventTime(): void {
    this.event.start = DateTime.fromISO(this.event.start, {
      zone: this.timezone,
    }).toString();
    this.event.end = DateTime.fromISO(this.event.end, {
      zone: this.timezone,
    }).toString();
    this.cd.detectChanges();
  }

  private setParticipants() {
    this.participants = this.event.attendees.map((a) => ({
      name: a.attendee?.name ?? a.user?.name ?? '',
      email: a.attendee?.email ?? a.user?.email ?? '',
      role: a.role || ERole.ATTENDEE,
    }));
  }
}
